import { createSelector } from "reselect";
import { AccountLOB, AccountStatus, AccountType, } from "../types";
import { getCustomizations } from "../../customization/selectors";
import { cdProductCodes, inactiveAccountStatus } from "./constants";
import { getAllAccounts } from "./selectors";
import { getByType, filterExpiredAccount, checkOwnershipType, filterByRetirementIndicator, formatDepositAccountsForDropdown, } from "./utils";
/**
 * Returns all deposit accounts or an empty array.
 */
const getAllDepositAccounts = createSelector(getAllAccounts, (accounts) => { var _a; return (_a = accounts === null || accounts === void 0 ? void 0 : accounts.filter((account) => account.lob === AccountLOB.DEPOSIT)) !== null && _a !== void 0 ? _a : []; });
const getOpenInternalDepositAccounts = createSelector(getAllDepositAccounts, (accounts) => {
    return accounts.filter((account) => {
        var _a;
        const { status } = account;
        return (status !== AccountStatus.CLOSED &&
            !!((_a = account.indicator) === null || _a === void 0 ? void 0 : _a.external) === false);
    });
});
const getDebitCardEligibleAccounts = createSelector(getOpenInternalDepositAccounts, getByType(AccountType.DDA, AccountType.MMA, AccountType.CMG));
/**
 * Returns all active accounts and any accounts closed within 60 days
 */
const getHasActiveDepositAccounts = createSelector(getAllDepositAccounts, (accounts) => accounts.some(filterExpiredAccount));
const getHasTrusteeOnlyDepositAccounts = createSelector(getAllDepositAccounts, (accounts) => {
    return accounts.length > 0 && accounts.every(checkOwnershipType);
});
/**
 * Returns true/false whether any deposit accounts have the `lastUpdated` property defined
 */
const getHasStaleAccounts = createSelector(getAllDepositAccounts, (accounts) => {
    return accounts.some((account) => !!account.lastUpdated);
});
const getNonRetirementAccounts = createSelector(getAllDepositAccounts, (accounts) => {
    return accounts.filter(filterByRetirementIndicator(false));
});
const getRetirementAccounts = createSelector(getAllDepositAccounts, (accounts) => {
    return accounts.filter(filterByRetirementIndicator(true));
});
const getNonRetirementAccountsByType = (accType) => createSelector(getNonRetirementAccounts, getByType(accType));
const getDDANonRetirement = getNonRetirementAccountsByType(AccountType.DDA);
const getMMANonRetirement = getNonRetirementAccountsByType(AccountType.MMA);
const getSDANonRetirement = getNonRetirementAccountsByType(AccountType.SDA);
const getCMGNonRetirement = getNonRetirementAccountsByType(AccountType.CMG);
const getCDANonRetirement = getNonRetirementAccountsByType(AccountType.CDA);
const getDepositAccountsForDropdown = createSelector(getDDANonRetirement, getMMANonRetirement, getSDANonRetirement, getCMGNonRetirement, getCDANonRetirement, getRetirementAccounts, (_, customizations) => getCustomizations(customizations), (...allSelectors) => {
    return formatDepositAccountsForDropdown(...allSelectors);
});
export const getManageableCDs = createSelector(getAllAccounts, (accounts) => {
    const toUpper = (status) => {
        return status.toUpperCase();
    };
    return accounts === null || accounts === void 0 ? void 0 : accounts.filter((account) => {
        return (account.productId &&
            cdProductCodes.includes(account.productId) &&
            !inactiveAccountStatus
                .map(toUpper)
                .includes(account.status.toUpperCase()) &&
            !account.closeDate);
    });
});
/**
 * Contains Deposit Account Information
 */
export default {
    getAll: getAllDepositAccounts,
    getOpenInternal: getOpenInternalDepositAccounts,
    getDebitCardEligible: getDebitCardEligibleAccounts,
    getIsActive: getHasActiveDepositAccounts,
    getHasTrusteeOnly: getHasTrusteeOnlyDepositAccounts,
    getHasStaleAccounts,
    getAllForDropdown: getDepositAccountsForDropdown,
    getManageableCDs,
    retirement: {
        getAll: getRetirementAccounts,
    },
    nonRetirement: {
        getAll: getNonRetirementAccounts,
        getByType: getNonRetirementAccountsByType,
        getDDA: getDDANonRetirement,
        getMMA: getMMANonRetirement,
        getSDA: getSDANonRetirement,
        getCMG: getCMGNonRetirement,
        getCDA: getCDANonRetirement,
    },
};
//# sourceMappingURL=deposit.js.map